import { cloneElement, useMemo, useState } from "react";
import {
	Grid,
	GridColumn,
	GridColumnProps,
	GridColumnReorderEvent,
	GridColumnResizeEvent,
	GridCellProps,
	GridCustomCellProps,
	GridDataStateChangeEvent,
	GridExpandChangeEvent,
	GridFilterCellProps,
	GridRowClickEvent,
	GridRowProps,
} from "@progress/kendo-react-grid";
import {
	CompositeFilterDescriptor,
	FilterDescriptor,
} from "@progress/kendo-data-query";
import { State, process } from "@progress/kendo-data-query";
import {
	setExpandedState,
	setGroupIds,
} from "@progress/kendo-react-data-tools";
import { Check, Columns3, StretchHorizontal, X } from "lucide-react";
import {
	setTableConfig,
	applyColumnLayout,
} from "../../components/custom-table/table-helpers";
import { articlesTableConfigDefault } from "./articles-table-config-default";
import { SearchInput } from "../../components/ui/search-input.component";
import { Button } from "../../components/ui/button";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Translate } from "../../components/translate/translate.component";
import { formatDate } from "../../lib/utils/utils-functions";
import {
	articleClusterOptions,
	articleTypeOptions,
} from "../../lib/constants/options";
import { ExtendedArticleEntityType } from "../../store/Jobs/types";
import { TableConfig } from "../../types/types";
import { ArticleExportDialog } from "./article-export-dialog.component";
import { getArticleExportValue } from "./article-export.utils";
import "./articles-table.css";

// The general search covers every visible column: each token must appear in
// the displayed value of some cell of the row.
const matchesSearch = (
	article: ExtendedArticleEntityType,
	tokens: string[],
	fields: string[]
): boolean =>
	tokens.every((token) =>
		fields.some((field) =>
			String(getArticleExportValue(article, field) ?? "")
				.toLowerCase()
				.includes(token)
		)
	);

const BooleanCell = (props: GridCellProps) => {
	const value = props.dataItem[props.field as string] as boolean | null;
	return (
		<td style={{ textAlign: "center" }}>
			<div className="flex justify-center">
				{value === true ? (
					<Check className="h-4 w-4 text-green-600" />
				) : value === false ? (
					<X className="h-4 w-4 text-gray-400" />
				) : (
					<span className="text-gray-400">-</span>
				)}
			</div>
		</td>
	);
};

const ClusterCell = (props: GridCellProps) => {
	const value = props.dataItem.cluster as string;
	const label =
		articleClusterOptions.find((option) => option.value === value)?.label ??
		value ??
		"";
	return <td>{label}</td>;
};

const TypeCell = (props: GridCellProps) => {
	const value = props.dataItem.type as number;
	const label =
		articleTypeOptions.find((option) => option.value === value)?.label ??
		String(value ?? "");
	return <td>{label}</td>;
};

const ModifiedAtCell = (props: GridCellProps) => {
	const article = props.dataItem as ExtendedArticleEntityType;
	return (
		<td>{article.custom ? formatDate(article.modified_at) : "Keine"}</td>
	);
};

// KendoReact also invokes a column's custom cell for group-header rows (with
// rowType "groupHeader"). Without this guard each custom cell renders a phantom
// <td> next to the colspan group-header cell, pushing the row past the table's
// column count and inflating the grouping row's height. Render only data rows;
// returning null for the rest matches Kendo's default (no cell at all).
const dataCellOnly =
	(render: (props: GridCellProps) => React.ReactElement) =>
	(props: GridCellProps) => (props.rowType === "data" ? render(props) : null);

const CELL_BY_FIELD: Record<
	string,
	(props: GridCellProps) => React.ReactElement | null
> = {
	is_req_dr_tax: dataCellOnly(BooleanCell),
	cluster: dataCellOnly(ClusterCell),
	type: dataCellOnly(TypeCell),
	modified_at: dataCellOnly(ModifiedAtCell),
};

// A grouped column is rendered as a zero-width carrier so it disappears from the
// body while staying in the grid's column set — without a column for that field
// Kendo's group-panel chip can't resolve the title and shows the raw field name.
const HiddenCarrierCell = (props: GridCellProps) =>
	props.rowType === "data" ? <td className="articles-hidden-col" /> : null;

const BOOLEAN_FIELDS = new Set(["is_req_dr_tax"]);

// Boolean columns store null when the flag was never set; grouped, that null
// forms its own empty group next to "Nein". Coerce null/undefined to false on
// the grouped boolean fields so null and false collapse into one "Nein" group.
const normalizeBooleanGroups = (
	rows: ExtendedArticleEntityType[],
	group: State["group"]
): ExtendedArticleEntityType[] => {
	const fields = (group ?? [])
		.map((descriptor) => descriptor.field)
		.filter(
			(field): field is string => !!field && BOOLEAN_FIELDS.has(field)
		);
	if (fields.length === 0) return rows;
	return rows.map((row) => {
		let next = row;
		for (const field of fields) {
			if ((next as Record<string, unknown>)[field] == null) {
				next = next === row ? { ...row } : next;
				(next as Record<string, unknown>)[field] = false;
			}
		}
		return next;
	});
};

const COLUMNS_STORAGE_KEY = "articles-table-config";
const PAGE_SIZE_STORAGE_KEY = "articles-table-page-size";

const readStoredConfig = (): TableConfig | null => {
	try {
		const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
		return raw ? (JSON.parse(raw) as TableConfig) : null;
	} catch {
		return null;
	}
};

const readStoredPageSize = (): number => {
	const stored = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
	return stored > 0 ? stored : 50;
};

// Every column filter is a plain "contains" search box. Filtering is applied
// manually on the displayed cell values (see getArticleExportValue), so it
// matches what the user sees and works for every column type.
const ContainsFilterCell = (props: GridFilterCellProps) => {
	const value = (props.value ?? "") as string;
	return (
		<span className="articles-filter">
			<input
				className="articles-filter-input"
				value={value}
				placeholder="Suchen"
				onChange={(event) =>
					props.onChange({
						value: event.target.value,
						operator: "contains",
						syntheticEvent: event,
					})
				}
			/>
			{value && (
				<button
					type="button"
					className="articles-filter-clear"
					aria-label="Filter löschen"
					onClick={(event) =>
						props.onChange({
							value: "",
							operator: "contains",
							syntheticEvent: event,
						})
					}
				>
					<X className="h-3 w-3" />
				</button>
			)}
		</span>
	);
};

// Flattens the grid's composite filter into a { field: searchText } map.
const collectColumnFilters = (
	filter: CompositeFilterDescriptor | undefined
): Record<string, string> => {
	const result: Record<string, string> = {};
	const walk = (node?: CompositeFilterDescriptor | FilterDescriptor) => {
		if (!node) return;
		if ("filters" in node) {
			node.filters.forEach(walk);
		} else if (
			node.field &&
			node.value != null &&
			String(node.value) !== ""
		) {
			result[node.field as string] = String(node.value);
		}
	};
	walk(filter);
	return result;
};

const GROUP_COLUMN_TITLES: Record<string, string> = Object.fromEntries(
	articlesTableConfigDefault.columns.map((column) => [
		column.field as string,
		column.title ?? (column.field as string),
	])
);

const resolveGroupValue = (field: string, value: unknown): string => {
	if (field === "cluster") {
		return (
			articleClusterOptions.find((o) => o.value === value)?.label ??
			String(value ?? "")
		);
	}
	if (field === "type") {
		return (
			articleTypeOptions.find((o) => o.value === value)?.label ??
			String(value ?? "")
		);
	}
	if (field === "is_req_dr_tax") {
		return value === true ? "Ja" : value === false ? "Nein" : "";
	}
	if (field === "modified_at") {
		return value ? formatDate(value as string) : "";
	}
	return value == null ? "" : String(value);
};

// Group header showing the column label (not the raw field key) and the
// displayed group value, rendered compactly so the group row stays short.
const ArticlesGroupHeaderCell = (props: GridCustomCellProps) => {
	if (!props.tdProps) return null;
	const field = props.dataItem.field as string;
	const title = GROUP_COLUMN_TITLES[field] ?? field;
	const value = resolveGroupValue(field, props.dataItem.value);
	// Inline styles (not CSS classes) so the slim layout is immune to Kendo's
	// theme cascade and to stale-CSS/HMR issues: the default group cell wraps a
	// 16px caret icon in an inline <p>, which inflates the line box well past a
	// plain-text data row. An inline-flex single line bounds the height to the
	// icon, keeping the group row at least as slim as the data rows.
	return (
		<td
			{...props.tdProps}
			style={{ ...(props.tdProps.style ?? {}), padding: "4px 6px" }}
		>
			<span
				style={{
					display: "inline-flex",
					alignItems: "baseline",
					gap: 6,
					lineHeight: 1.2,
				}}
			>
				{/* @ts-expect-error the expand toggle is the first default child */}
				{props.children?.props?.children?.[0]}
				{/* The grouped column repeats on every band, so keep it as a small
				    muted eyebrow and let the value be the bold focus. */}
				<span className="text-[11px] uppercase tracking-wide text-gray-400">
					{title}
				</span>
				{value !== "" && (
					<span className="font-semibold text-gray-800">{value}</span>
				)}
			</span>
		</td>
	);
};

interface ArticlesTableProps {
	articles: ExtendedArticleEntityType[];
	onRowClick: (article: ExtendedArticleEntityType) => void;
	actions?: React.ReactNode;
}

export const ArticlesTable: React.FC<ArticlesTableProps> = ({
	articles,
	onRowClick,
	actions,
}) => {
	// Column layout (visibility/order/width) is persisted per browser in
	// localStorage; the entries-per-page choice is persisted alongside it.
	const [articlesConfig, setArticlesConfig] = useState<TableConfig | null>(
		readStoredConfig
	);
	const updateArticlesConfig = (config: TableConfig) => {
		setArticlesConfig(config);
		try {
			localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(config));
		} catch {
			// ignore quota / privacy-mode errors
		}
	};

	const [searchTerm, setSearchTerm] = useState("");

	const tableConfig = useMemo<TableConfig>(
		() => setTableConfig(articlesTableConfigDefault, articlesConfig),
		[articlesConfig]
	);

	const [dataState, setDataState] = useState<State>({
		skip: 0,
		take: readStoredPageSize(),
		sort: [],
		group: [],
	});
	const [collapsedGroupIds, setCollapsedGroupIds] = useState<string[]>([]);

	const visibleSearchFields = useMemo(
		() =>
			tableConfig.columns
				.filter(
					(column) => column.field && (column as any).show !== false
				)
				.map((column) => column.field as string),
		[tableConfig]
	);

	const filteredArticles = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		if (!term) return articles;
		const tokens = term.split(/\s+/);
		return articles.filter((article) =>
			matchesSearch(article, tokens, visibleSearchFields)
		);
	}, [articles, searchTerm, visibleSearchFields]);

	const columnFilters = useMemo(
		() => collectColumnFilters(dataState.filter),
		[dataState.filter]
	);

	// Apply the per-column "contains" searches against the displayed values.
	const applyColumnFilters = (rows: ExtendedArticleEntityType[]) => {
		const fields = Object.keys(columnFilters);
		if (fields.length === 0) return rows;
		return rows.filter((article) =>
			fields.every((field) =>
				String(getArticleExportValue(article, field) ?? "")
					.toLowerCase()
					.includes(columnFilters[field].toLowerCase())
			)
		);
	};

	const isGrouped = (dataState.group?.length ?? 0) > 0;

	const processedData = useMemo(() => {
		const rows = normalizeBooleanGroups(
			applyColumnFilters(filteredArticles),
			dataState.group
		);
		const result = process(rows, {
			skip: dataState.skip,
			take: dataState.take,
			sort: dataState.sort,
			group: dataState.group,
		});
		if (isGrouped) {
			setGroupIds({ data: result.data, group: dataState.group ?? [] });
			result.data = setExpandedState({
				data: result.data,
				collapsedIds: collapsedGroupIds,
			});
		}
		return result;
	}, [
		filteredArticles,
		dataState,
		columnFilters,
		isGrouped,
		collapsedGroupIds,
	]);

	const handleSearchChange = (value: string) => {
		setSearchTerm(value);
		setDataState((prev) => ({ ...prev, skip: 0 }));
	};

	const handleDataStateChange = (event: GridDataStateChangeEvent) => {
		if (event.dataState.take && event.dataState.take !== dataState.take) {
			try {
				localStorage.setItem(
					PAGE_SIZE_STORAGE_KEY,
					String(event.dataState.take)
				);
			} catch {
				// ignore quota / privacy-mode errors
			}
		}
		setDataState(event.dataState);
	};

	const persistLayout = (eventColumns: GridColumnProps[]) => {
		// The grouped column is rendered as a zero-width carrier; never persist
		// that 0px width back into the config or the column would stay collapsed
		// once it is ungrouped again.
		const groupedFields = new Set(
			(dataState.group ?? []).map((descriptor) => descriptor.field)
		);
		const layoutColumns = eventColumns.filter(
			(column) => !groupedFields.has(column.field as string)
		);
		updateArticlesConfig({
			columns: applyColumnLayout(tableConfig.columns, layoutColumns),
			dataState: {},
		});
	};

	const handleColumnReorder = (event: GridColumnReorderEvent) => {
		persistLayout(event.columns);
	};

	const handleColumnResize = (event: GridColumnResizeEvent) => {
		if (event.end) persistLayout(event.columns);
	};

	const handleToggleColumn = (field: string, show: boolean) => {
		const newColumns = tableConfig.columns.map((column) =>
			column.field === field ? { ...column, show } : column
		);
		updateArticlesConfig({ columns: newColumns, dataState: {} });
	};

	// Resize every visible column to fit its header and content, by measuring
	// the displayed text width with the grid's actual font.
	const handleAutoFitColumns = () => {
		const ctx = document.createElement("canvas").getContext("2d");
		if (!ctx) return;

		const sample = document.querySelector<HTMLElement>(".articles-grid td");
		const styles = sample ? getComputedStyle(sample) : null;
		const fontSize = styles?.fontSize ?? "13px";
		const fontFamily = styles?.fontFamily ?? "sans-serif";

		const visibleColumns = tableConfig.columns.filter(
			(column) => (column as any).show !== false
		);

		const widths: Record<string, number> = {};
		ctx.font = `600 ${fontSize} ${fontFamily}`;
		for (const column of visibleColumns) {
			// header text + space for the sort indicator
			widths[column.field as string] =
				ctx.measureText(column.title ?? "").width + 32;
		}

		ctx.font = `${fontSize} ${fontFamily}`;
		for (const article of articles) {
			for (const column of visibleColumns) {
				const field = column.field as string;
				const text = String(
					getArticleExportValue(article, field) ?? ""
				);
				const width = ctx.measureText(text).width + 18;
				if (width > widths[field]) widths[field] = width;
			}
		}

		// Cap the width so long-text columns wrap instead of stretching to fit
		// their longest value; short columns still shrink to their content.
		const MAX_AUTO_WIDTH = 180;
		const fitted: Record<string, number> = {};
		for (const column of visibleColumns) {
			const field = column.field as string;
			fitted[field] = Math.min(
				Math.max(Math.ceil(widths[field] ?? 80), 50),
				MAX_AUTO_WIDTH
			);
		}

		// Never leave unused space: if the fitted columns don't fill the grid,
		// scale them up proportionally so they span the whole table width.
		const gridContent = document.querySelector<HTMLElement>(
			".articles-grid .k-grid-content"
		);
		const available = gridContent?.clientWidth ?? 0;
		const total = Object.values(fitted).reduce((sum, w) => sum + w, 0);
		if (available > 0 && total > 0 && total < available) {
			const scale = available / total;
			for (const field of Object.keys(fitted)) {
				fitted[field] = Math.floor(fitted[field] * scale);
			}
		}

		const newColumns = tableConfig.columns.map((column) => {
			if ((column as any).show === false) return column;
			return {
				...column,
				width: `${fitted[column.field as string] ?? 80}px`,
			};
		});
		updateArticlesConfig({ columns: newColumns, dataState: {} });
	};

	const toggleGroupCollapse = (groupId: string) => {
		setCollapsedGroupIds((prev) =>
			prev.includes(groupId)
				? prev.filter((id) => id !== groupId)
				: [...prev, groupId]
		);
	};

	// The caret fires onExpandChange (it does not bubble to onRowClick), the rest
	// of the group-header row fires onRowClick — both routed through the same
	// toggle, so neither double-toggles the other.
	const handleExpandChange = (event: GridExpandChangeEvent) => {
		const groupId = event.dataItem.groupId;
		if (groupId) toggleGroupCollapse(groupId);
	};

	const getExportArticles = () =>
		process(applyColumnFilters(filteredArticles), {
			sort: dataState.sort,
		}).data as ExtendedArticleEntityType[];

	const renderColumns = () => {
		// A grouped column's value already shows in the group header. We still
		// render it (as a zero-width carrier) instead of dropping it, so the
		// group-panel chip can resolve its proper title instead of the field key.
		const groupedFields = new Set(
			(dataState.group ?? []).map((group) => group.field)
		);
		return tableConfig.columns
			.filter((column) => (column as any).show !== false)
			.map((column) => {
				const field = column.field as string;
				if (groupedFields.has(field)) {
					return (
						<GridColumn
							key={field}
							field={field}
							title={column.title}
							orderIndex={column.orderIndex}
							width="0px"
							className="articles-hidden-col"
							headerClassName="articles-hidden-col"
							cell={HiddenCarrierCell}
							groupable={false}
							sortable={false}
							resizable={false}
							reorderable={false}
							filterable={false}
						/>
					);
				}
				return (
					<GridColumn
						key={field}
						field={field}
						title={column.title}
						width={column.width}
						orderIndex={column.orderIndex}
						cell={CELL_BY_FIELD[field]}
						filterCell={ContainsFilterCell}
					/>
				);
			});
	};

	const rowRender = (
		trElement: React.ReactElement<HTMLTableRowElement>,
		props: GridRowProps
	) => {
		const isGroup =
			(props.dataItem as { groupId?: string }).groupId != null;
		const article = props.dataItem as ExtendedArticleEntityType;
		const trProps: any = {
			style: { ...trElement.props.style, cursor: "pointer" },
			...(isGroup
				? {}
				: {
						"data-testid": `article-row-${article.id}`,
						"aria-label": `Artikel ${article.code_e}`,
					}),
		};
		return cloneElement(
			trElement,
			{ ...trProps },
			// @ts-expect-error this is how kendo react does it in their guides
			trElement.props.children
		);
	};

	return (
		<div className="flex flex-col flex-1 min-h-0 bg-white">
			<div className="p-3 border-b flex items-center gap-3 shrink-0">
				<div
					className="flex-1 min-w-0 max-w-md"
					data-testid="articles-table-search"
					aria-label="Artikel durchsuchen"
				>
					<SearchInput
						value={searchTerm}
						onChange={handleSearchChange}
						placeholder="Alle Spalten durchsuchen ..."
						shortcut="k"
					/>
				</div>
				<div className="ml-auto flex items-center gap-3">
					{actions}
					<ArticleExportDialog
						columns={tableConfig.columns}
						getArticles={getExportArticles}
						exportInfo={{
							searchTerm: searchTerm.trim(),
							columnFilters,
							sort: dataState.sort ?? [],
							totalCount: articles.length,
						}}
					/>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="outline"
								size="sm"
								aria-label="Spalten konfigurieren"
								data-testid="articles-table-columns-button"
							>
								<Columns3 className="w-4 h-4 mr-2" />
								Spalten
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							align="end"
							className="max-h-96 overflow-y-auto"
						>
							<DropdownMenuItem
								onSelect={handleAutoFitColumns}
								aria-label="Spaltenbreite an Inhalt anpassen"
								data-testid="articles-table-autofit-item"
							>
								<StretchHorizontal className="w-4 h-4 mr-2" />
								Spaltenbreite anpassen
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuLabel>
								Spalten anzeigen
							</DropdownMenuLabel>
							<DropdownMenuSeparator />
							{tableConfig.columns.map((column) => (
								<DropdownMenuCheckboxItem
									key={column.field}
									checked={(column as any).show !== false}
									onCheckedChange={(checked) =>
										handleToggleColumn(
											column.field!,
											checked
										)
									}
									onSelect={(event) => event.preventDefault()}
								>
									{column.title}
								</DropdownMenuCheckboxItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>

			<div className="flex-1 min-h-0 px-3 pb-3">
				<Translate>
					<Grid
						className="articles-grid"
						style={{ height: "100%" }}
						data={processedData.data}
						total={processedData.total}
						skip={dataState.skip}
						take={dataState.take}
						sort={dataState.sort}
						group={dataState.group}
						filter={dataState.filter}
						sortable
						reorderable
						resizable
						groupable
						filterable
						expandField="expanded"
						pageable={{
							pageSizes: [25, 50, 100, 200],
							buttonCount: 5,
						}}
						size="small"
						onDataStateChange={handleDataStateChange}
						onExpandChange={handleExpandChange}
						onColumnReorder={handleColumnReorder}
						onColumnResize={handleColumnResize}
						onRowClick={(event: GridRowClickEvent) => {
							// Group-header rows carry a groupId and no article id;
							// clicking one toggles the group instead of trying to
							// open a non-existent article. Toggling lives only here
							// (not in onExpandChange) so a caret click — which also
							// bubbles to the row — doesn't toggle twice.
							const groupId = (
								event.dataItem as { groupId?: string }
							).groupId;
							if (groupId != null) {
								toggleGroupCollapse(groupId);
								return;
							}
							onRowClick(event.dataItem);
						}}
						rowRender={rowRender}
						cells={{
							group: {
								groupHeader: ArticlesGroupHeaderCell as any,
							},
						}}
					>
						{renderColumns()}
					</Grid>
				</Translate>
			</div>
		</div>
	);
};
